import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { AlertCircle, CalendarClock, CreditCard, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { formatPence } from "@pgs-core/portal-lib/format";
import {
  billingStatusBadgeVariant,
  billingStatusLabel,
  cadenceLabel,
  cadencePeriodNoun,
} from "@pgs-core/portal-lib/billing";
import {
  completeMandateFlow,
  fetchBillingSummary,
  startMandateFlow,
} from "../lib/billing";
import { BillingTabs } from "../components/billing-tabs";
import type { ClientOutletContext } from "../lib/outlet-context";

/**
 * Billing → Overview: the three at-a-glance boxes. Plan and next-payment
 * figures reflect the tenant's real bill — including any discount lines and
 * the adjusted totals the server computes. The Plan tab carries the same
 * breakdown alongside the forecast.
 */
export function BillingPage() {
  const { api, refreshStatus, setBreadcrumbs } = useOutletContext<ClientOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Billing" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  const summaryQuery = useApiQuery(() => (api ? fetchBillingSummary(api) : null), [api]);
  const summary = summaryQuery.data;

  // Returning from GoCardless: ?redirect_flow_id=… means the client completed
  // the mandate pages — finish the flow once, then clean the URL.
  const completedFlow = useRef("");
  useEffect(() => {
    const flowId = searchParams.get("redirect_flow_id");
    if (!api || !flowId || completedFlow.current === flowId) return;
    completedFlow.current = flowId;
    setWorking(true);
    completeMandateFlow(api, flowId)
      .then(() => {
        setNotice("Your Direct Debit is set up — thank you.");
        setSearchParams({}, { replace: true });
        summaryQuery.reload();
        refreshStatus();
      })
      .catch((err: Error) => setActionError(err.message))
      .finally(() => setWorking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, searchParams]);

  const setUpDirectDebit = async () => {
    if (!api || working) return;
    setWorking(true);
    setActionError("");
    try {
      const flow = await startMandateFlow(api, `${window.location.origin}/billing`);
      window.location.href = flow.redirect_url;
    } catch (err) {
      setActionError((err as Error).message);
      setWorking(false);
    }
  };

  const pastDue = summary?.billing_status === "past_due";

  return (
    <div>
      <BillingTabs />

      <PageHeader
        title="Overview"
        description="Your plan, payment method and next payment at a glance."
      />

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert className="mb-6">
          <CreditCard className="h-4 w-4" />
          <AlertTitle>All set</AlertTitle>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}
      {summaryQuery.error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>{summaryQuery.error}</AlertDescription>
        </Alert>
      )}
      {pastDue && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Payment overdue</AlertTitle>
          <AlertDescription>
            Your latest payment didn't go through. Updating your Direct Debit below
            usually fixes this — or get in touch and we'll sort it together.
          </AlertDescription>
        </Alert>
      )}

      {!summary && !summaryQuery.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {summary && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Your plan</CardTitle>
              <CardDescription>
                What makes up your {cadenceLabel(summary.billing_cadence).toLowerCase()} bill.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="space-y-1 text-sm">
                {summary.lines.map((line) => (
                  <div key={`${line.kind}-${line.slug ?? line.description}`} className="flex justify-between">
                    <dt className="text-muted-foreground">{line.description}</dt>
                    <dd>{formatPence(line.amount_pence)}</dd>
                  </div>
                ))}
                {summary.vat_pence > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">VAT ({(summary.vat_rate_bps / 100).toFixed(0)}%)</dt>
                    <dd>{formatPence(summary.vat_pence)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t pt-1 font-semibold">
                  <dt>Total per {cadencePeriodNoun(summary.billing_cadence)}</dt>
                  <dd>{formatPence(summary.total_pence)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Payment method</CardTitle>
              <Badge variant={billingStatusBadgeVariant(summary.billing_status)}>
                {billingStatusLabel(summary.billing_status)}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {summary.mandate_active
                  ? "You pay by Direct Debit. Update it any time — for example after changing bank."
                  : "No Direct Debit set up yet. It takes about two minutes."}
              </p>
              <Button size="sm" disabled={working || !api} onClick={setUpDirectDebit}>
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {summary.mandate_active ? "Update Direct Debit" : "Set up Direct Debit"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Next payment</CardTitle>
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary.subscription_active ? formatPence(summary.total_pence) : "—"}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {summary.subscription_active
                  ? summary.next_charge_date
                    ? `Collected on ${summary.next_charge_date}`
                    : `Collected ${summary.billing_cadence === "annual" ? "annually" : "monthly"} by Direct Debit`
                  : "No subscription is active yet."}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
